import { FormEvent, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  CurrentsAPI,
  type AIJob,
  type OceanAgent,
  type OceanMissionStep,
  type OceanMissionSummary,
} from "@vida/currents-core";
import { MissionTracePanel } from "../components/agent-test/MissionTracePanel";
import { resolveJobOutputText } from "../services/jobOutput";
import { createCurrentsApiConfig } from "../services/currentsSession";
import { resolveUsers } from "../api";
import {
  buildMissionSummaryIo,
  formatModelDisplay,
  parseMissionOriginFromJob,
  parseMissionOriginFromSummary,
  parseMissionStepIo,
  resolveOriginUserSub,
  safeParseJson,
  type MissionIoBlock,
  type MissionOrigin,
} from "../utils/missionTrace";

const TERMINAL: AIJob["status"][] = ["COMPLETED", "FAILED", "CANCELLED", "TIMED_OUT"];

export function AgentTestPage() {
  const { agentId } = useParams();
  const [searchParams] = useSearchParams();
  const initialJobId = searchParams.get("jobId");
  const fromInsights = searchParams.get("from") === "insights" || Boolean(initialJobId);
  const [agent, setAgent] = useState<OceanAgent | null>(null);
  const [goal, setGoal] = useState("Riassumi le mie note recenti e cerca fonti web pertinenti.");
  const [job, setJob] = useState<AIJob | null>(null);
  const [steps, setSteps] = useState<OceanMissionStep[]>([]);
  const [missionOutputText, setMissionOutputText] = useState<string | null>(null);
  const [missionSummary, setMissionSummary] = useState<OceanMissionSummary | null>(null);
  const [missionOrigin, setMissionOrigin] = useState<MissionOrigin | null>(null);
  const [resolvedUser, setResolvedUser] = useState<{ displayName?: string | null; email?: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!agentId) return;
    void (async () => {
      try {
        const a = await CurrentsAPI.getAgent(createCurrentsApiConfig(), agentId);
        setAgent(a);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [agentId]);

  // Carica la trace di una missione esistente quando si arriva da Insights (?jobId=).
  useEffect(() => {
    if (!initialJobId) return;
    void refreshJob(initialJobId).catch((e) => {
      setError(e instanceof Error ? e.message : String(e));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialJobId]);

  async function refreshJob(jobId: string) {
    const cfg = createCurrentsApiConfig();
    const j = await CurrentsAPI.getJob(cfg, jobId);
    if (j) setJob(j);
    const s = await CurrentsAPI.listMissionSteps(cfg, jobId);
    setSteps(s);

    let summary: OceanMissionSummary | null = null;
    if (fromInsights) {
      const missions = await CurrentsAPI.listMissions(cfg, { limit: 100 });
      summary = missions.find((m) => m.jobId === jobId) ?? null;
      setMissionSummary(summary);
    } else {
      setMissionSummary(null);
    }

    const origin = summary
      ? parseMissionOriginFromSummary({
          ...summary,
          goalPreview: summary.goalPreview || j?.prompt || undefined,
        })
      : j
        ? parseMissionOriginFromJob(j)
        : null;
    if (origin && !origin.userQuestion && j?.prompt) {
      origin.userQuestion = j.prompt.trim();
    }
    if (origin && !origin.endedAt && j?.completedAt) {
      origin.endedAt = j.completedAt;
    }
    setMissionOrigin(origin);

    const userSub = origin ? resolveOriginUserSub(origin) : null;
    if (userSub) {
      try {
        const users = await resolveUsers([userSub]);
        setResolvedUser(users[0] ?? null);
      } catch {
        setResolvedUser(null);
      }
    } else {
      setResolvedUser(null);
    }

    const missionError = s.find((step) => step.kind === "error");
    if (missionError?.detailJson) {
      const detail = safeParseJson(missionError.detailJson) as { message?: string } | null;
      if (detail && typeof detail === "object" && detail.message) {
        setError(detail.message);
      }
    }

    if (j && TERMINAL.includes(j.status)) {
      const out = await resolveJobOutputText(j);
      setMissionOutputText(out);
    } else {
      const completeStep = s.find((step) => step.kind === "complete");
      if (completeStep?.detailJson) {
        const detail = safeParseJson(completeStep.detailJson) as { output?: string } | null;
        if (detail && typeof detail === "object" && detail.output?.trim()) {
          setMissionOutputText(detail.output.trim());
        }
      }
    }

    const terminal = (j && TERMINAL.includes(j.status)) || Boolean(missionError);
    if (terminal) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setRunning(false);
    }
  }

  async function onRun(e: FormEvent) {
    e.preventDefault();
    if (!agentId) return;
    setRunning(true);
    setError(null);
    setJob(null);
    setSteps([]);
    setMissionOutputText(null);
    setMissionSummary(null);
    setMissionOrigin(null);
    setResolvedUser(null);
    try {
      const started = await CurrentsAPI.invokeAgent(
        createCurrentsApiConfig(),
        agentId,
        goal.trim(),
      );
      setJob(started);
      pollRef.current = setInterval(() => {
        void refreshJob(started.jobId).catch((pollErr) => {
          setError(pollErr instanceof Error ? pollErr.message : String(pollErr));
        });
      }, 2000);
      await refreshJob(started.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setRunning(false);
    }
  }

  const parsedSteps = steps.map(parseMissionStepIo);
  let missionInput: MissionIoBlock[] = [];
  let missionOutput: MissionIoBlock[] = [];
  if (job) {
    const summary = buildMissionSummaryIo(job, missionOutputText);
    missionInput = summary.input;
    missionOutput = summary.output;
  }

  if (loading) {
    return (
      <div className="page">
        <p className="muted">Caricamento…</p>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="page">
        <p>Agente non trovato.</p>
        <Link to="/agents">Torna alla lista</Link>
      </div>
    );
  }

  return (
    <div className="page">
      {fromInsights && (
        <nav className="trace-back-nav">
          <Link className="btn secondary" to="/insights">
            ← Insights
          </Link>
        </nav>
      )}
      <header className="page-header">
        <div>
          <h1>{fromInsights ? "Trace" : "Test"} — {agent.name}</h1>
          <p className="muted">
            <span className="chip">{agent.role}</span>{" "}
            <span title={agent.modelId}>{formatModelDisplay(agent.modelId).short}</span>
          </p>
        </div>
        {!fromInsights && (
          <Link className="btn secondary" to={`/agents/${agent.id}/edit`}>
            Modifica
          </Link>
        )}
      </header>

      {!fromInsights && (
        <form className="card" onSubmit={(e) => void onRun(e)}>
          <label htmlFor="goal">Obiettivo missione</label>
          <textarea id="goal" value={goal} onChange={(e) => setGoal(e.target.value)} required />
          <div style={{ marginTop: "1rem" }}>
            <button type="submit" disabled={running}>
              {running ? "Esecuzione…" : "Esegui / Testa"}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="card">
          <p style={{ color: "#b91c1c" }}>{error}</p>
        </div>
      )}

      {(job || steps.length > 0) && (
        <MissionTracePanel
          missionInput={missionInput}
          missionOutput={missionOutput}
          steps={parsedSteps}
          missionAgentName={agent.name}
          missionOrigin={missionOrigin}
          resolvedUser={resolvedUser}
          pricingUnmatched={missionSummary?.pricingUnmatched}
          missionMarkup={missionSummary?.markup}
          defaultView={fromInsights ? "timeline" : "list"}
        />
      )}

      {job && !fromInsights && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Job {job.jobId}</h2>
          <p>
            Stato: <strong>{job.status}</strong>
            {job.progress != null && <> — {job.progress}%</>}
            {job.costCents != null && <> — {job.costCents}¢</>}
          </p>
          {job.errorMessage && <p style={{ color: "#b91c1c" }}>{job.errorMessage}</p>}
        </div>
      )}

      {job && fromInsights && (
        <p className="muted trace-job-compact">
          Job <code>{job.jobId}</code> · <strong>{job.status}</strong>
          {job.progress != null && <> · {job.progress}%</>}
          {job.costCents != null && <> · {job.costCents}¢</>}
          {job.errorMessage && <> — {job.errorMessage}</>}
        </p>
      )}
    </div>
  );
}
